import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from '../components/Links';
import { generatePassword, UserRow, users } from '../api';
import { useAuth } from '../auth';
import { ago, dayLabel } from '../format';
import { PasswordField } from './AdminUsers';

export function AdminUser() {
  const id = Number(useParams().id);
  const { user: me } = useAuth();
  const [u, setU] = useState<UserRow | null>(null);
  const [form, setForm] = useState({ name: '', role: 'staff' as 'admin' | 'staff' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetDone, setResetDone] = useState('');
  const [busy, setBusy] = useState(false);
  const isMe = id === me?.id;

  const load = useCallback(async () => {
    try {
      const found = (await users.list()).users.find((x) => x.id === id) ?? null;
      setU(found);
      if (found) setForm({ name: found.name, role: found.role === 'admin' ? 'admin' : 'staff' });
      if (!found) setError('No such user.');
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const save = async (disabled: boolean, message: string) => {
    if (!u) return;
    setBusy(true);
    setError('');
    try {
      await users.update(u.id, { ...form, disabled });
      setNotice(message);
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };

  const reset = async (e: FormEvent) => {
    e.preventDefault();
    if (!u) return;
    setBusy(true);
    setError('');
    try {
      await users.resetPassword(u.id, newPassword);
      setResetDone(newPassword);
      setNewPassword('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };

  if (!u) return <div className="page"><p><Link to="/admin/users">← Users</Link></p>{error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>}</div>;

  return (
    <div className="page">
      <p><Link to="/admin/users">← Users</Link></p>
      <h1>{u.name}{isMe && <span className="muted"> (you)</span>}</h1>
      <p className="muted small">
        {u.email} · {u.disabled ? 'Disabled' : u.mustChangePassword ? 'Must set their password at next login' : 'Active'}
        {' · '}added {u.createdAt ? dayLabel(u.createdAt) : '–'} · last login {u.lastLogin ? ago(u.lastLogin) : 'never'}
        {u.activeSessions ? ` · logged in on ${u.activeSessions} device${u.activeSessions > 1 ? 's' : ''}` : ''}
      </p>
      {notice && <p className="notice" role="status">{notice}</p>}
      {error && <p className="error" role="alert">{error}</p>}

      <form className="card form" onSubmit={(e) => { e.preventDefault(); void save(u.disabled, 'Saved.'); }}>
        <h2>Details</h2>
        <div className="grid2">
          <label>Name<input id="ue-name" type="text" required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
          <label>Role
            <select id="ue-role" value={form.role} disabled={isMe} onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'staff' })}>
              <option value="staff">Staff: live map and journeys</option>
              <option value="admin">Admin: also vehicles and users</option>
            </select>
          </label>
        </div>
        {isMe && <p className="muted small">You can't change your own role. Ask another admin.</p>}
        <div className="actions"><button className="primary" disabled={busy}>Save</button></div>
      </form>

      {!isMe && (
        <section className="card form">
          <h2>Access</h2>
          {u.disabled ? (
            <>
              <p>This user can't log in.</p>
              <div className="actions"><button className="primary" disabled={busy} onClick={() => void save(false, `${u.name} can log in again.`)}>Enable user</button></div>
            </>
          ) : (
            <>
              <p className="muted small">Disabling logs them out on every device straight away. Their history stays; you can enable them again later.</p>
              <div className="actions">
                <button className="danger-button" disabled={busy}
                  onClick={() => window.confirm(`Disable ${u.name}? They'll be logged out everywhere.`) && void save(true, `${u.name} is disabled and has been logged out.`)}>
                  Disable user
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {!isMe ? (
        <form className="card form" onSubmit={reset}>
          <h2>Reset password</h2>
          <p className="muted small">Sets a new starting password and logs them out everywhere. They choose their own at the next login.</p>
          <div className="grid2"><PasswordField id="ue-password" value={newPassword} onChange={setNewPassword} /></div>
          {resetDone && (
            <div className="callout" role="status">
              <strong>Password reset.</strong>
              <p>Give {u.name} this starting password: <span className="mono"><b>{resetDone}</b></span>. It isn't shown again.</p>
            </div>
          )}
          <div className="actions">
            {!newPassword && <button type="button" className="link" onClick={() => setNewPassword(generatePassword())}>Generate one</button>}
            <button className="primary" disabled={busy || !newPassword}>Reset password</button>
          </div>
        </form>
      ) : (
        <p><Link to="/account">Change your own password under My account →</Link></p>
      )}
    </div>
  );
}
